import { getUsers, deleteUser } from "@/api/userApi";
import { EnhancedDataTable } from "adizen-tanstack-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Eye,
  Pencil,
  Trash2,
  MoreHorizontal,
  AlertTriangle,
} from "lucide-react";
import { authStore } from "@/stores/authStore";
import { permissionStore, PermissionAction, ResourceType } from "@/stores/permissionStore";
import { useSnapshot } from "valtio";
import { getApiErrorMessage } from "@/utils/apiErrorMessage";
import { RANK_LABELS } from "@/utils/constants";

const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(" ");
    if (words.length === 1) return words[0][0].toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
};

export default function ManageUsers() {
  const snap = useSnapshot(authStore);
  const permSnap = useSnapshot(permissionStore);
  const navigate = useNavigate();
  const isSuperAdmin = snap?.user?.role === "SUPER_ADMIN";
  
  // Permission checks
  const canCreateUser = permSnap.can(PermissionAction.CREATE, ResourceType.USER);
  const canUpdateUser = permSnap.can(PermissionAction.UPDATE, ResourceType.USER);
  const canDeleteUser = permSnap.can(PermissionAction.DELETE, ResourceType.USER);
  const canReadUser = permSnap.can(PermissionAction.READ, ResourceType.USER);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Rows from the last successful fetch, for row-level actions that need the
  // user's name without triggering a render.
  const rowsRef = useRef([]);
  // Bumped to force the table to remount and refetch after a mutation.
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const refreshTable = useCallback(() => setTableRefreshKey((k) => k + 1), []);

  // Must be referentially stable: the table's internal fetch effect keys off
  // this callback's identity, so a new function per render causes duplicate
  // requests.
  const fetchUsers = useCallback(async (params) => {
    try {
      // Extract direct parameters from the simplified format
      const {
        page = 1,
        limit = 10,
        search,
        sortBy,
        sortOrder,
        role,
        status,
        emailVerified,
        accountSetup,
        gender,
        rankDesignation,
        ban,
        age,
        ageMin,
        ageMax,
        loginCount,
        createdAfter,
        createdBefore,
        lastLoginAfter,
        lastLoginBefore,
        ...otherParams
      } = params || {};

      const filters = {
        page,
        limit,
        sortBy: sortBy || "createdAt",
        sortOrder: sortOrder || "desc",
      };

      // Add search if provided
      if (search) {
        filters.search = search;
      }

      // Handle role filter
      if (role && role !== "all") {
        filters.roles = [role.toUpperCase()];
      }

      // Handle status filter (maps to isActive and ban)
      if (status && status !== "all") {
        if (status === "banned") {
          filters.ban = true;
        }
      }

      // Handle emailVerified filter
      if (emailVerified && emailVerified !== "all") {
        filters.emailVerified = emailVerified === "verified";
      }

      // Handle accountSetup filter
      if (accountSetup && accountSetup !== "all") {
        filters.accountSetupCompleted = accountSetup === "completed";
      }

      // Handle gender filter
      if (gender && gender !== "all") {
        filters.gender = gender.toUpperCase();
      }

      // Handle rankDesignation filter
      if (rankDesignation && rankDesignation !== "all") {
        filters.rankDesignation = rankDesignation;
      }

      // Handle ban filter (if directly provided)
      if (ban && ban !== "all") {
        filters.ban = ban === "banned";
      }

      // Handle age filters
      if (age && parseInt(age) > 0) {
        filters.ageMin = parseInt(age);
        filters.ageMax = parseInt(age);
      }

      if (loginCount) {
        filters.loginCount = parseInt(loginCount);
      }

      // Handle date filters
      if (createdAfter) {
        const startOfDay = new Date(createdAfter);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(createdAfter);
        endOfDay.setHours(23, 59, 59, 999);
        filters.createdAfter = startOfDay;
        filters.createdBefore = endOfDay;
      }

      if (createdBefore) {
        const startOfDay = new Date(createdBefore);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(createdBefore);
        endOfDay.setHours(23, 59, 59, 999);

        filters.createdAfter = startOfDay;
        filters.createdBefore = endOfDay;
      }

      if (lastLoginAfter) {
        const startOfDay = new Date(lastLoginAfter);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(lastLoginAfter);
        endOfDay.setHours(23, 59, 59, 999);
        filters.lastLoginAfter = startOfDay;
        filters.lastLoginBefore = endOfDay;
      }
      if (lastLoginBefore) {
        filters.lastLoginBefore = new Date(lastLoginBefore);
        filters.lastLoginAfter = new Date(lastLoginBefore).setHours(0, 0, 0, 0);
      }

      // Add any other parameters that weren't explicitly handled
      Object.keys(otherParams).forEach((key) => {
        if (otherParams[key] && otherParams[key] !== "all") {
          filters[key] = otherParams[key];
        }
      });
      const response = await getUsers(filters);

      if (response.success && Array.isArray(response.data)) {
        const mappedUsers = response.data.map((user) => ({
          id: user.id,
          name: user.name || user.email.split("@")[0],
          email: user.email,
          phone: user.phone || user.mobileNumber || "-",
          cdcNumber: user.indianCdc || "-",
          aadharNumber: user.aadhaarNumber || "-",
          mobileNumber: user.mobileNumber || "-",
          sidNumber: user.seafarerSid || "-",
          rank: user.rankDesignation || "-",
          gender: user.gender?.toLowerCase() || "-",
          age: user.age || "-",
          role: user.role?.toLowerCase() || "seafarer",
          status: user.ban
            ? "banned"
            : user.isActive !== false
              ? "active"
              : "inactive",
          emailVerified: user.emailVerified ? "verified" : "unverified",
          accountSetup: user.accountSetupCompleted ? "completed" : "pending",
          profileImage: user.profileImage ? import.meta.env.VITE_CDN_LINK + user.profileImage : null,
          lastActive: user.lastLoginAt || "-",
          joinedDate: user.createdAt,
          ship: user.currentShipName || "-",
          loginCount: user.loginCount || 0,
          banReason: user.banReason || "-",
          // Raw user data for actions
          _raw: user,
        }));

        rowsRef.current = mappedUsers;

        return {
          success: true,
          message: response.message,
          data: mappedUsers,
          meta: {
            total: response.pagination?.total || mappedUsers.length,
            page: response.pagination?.page,
            limit: response.pagination?.limit,
            totalPages: response.pagination?.totalPages,
            hasNext: response.pagination?.hasNext || false,
            hasPrev: response.pagination?.hasPrev || false,
          },
        };
      } else {
        throw new Error(response.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error(getApiErrorMessage(error, "Failed to fetch users"));
      return {
        success: false,
        message: error.message,
        data: [],
        meta: {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      };
    }
  }, []);

  // Built here rather than via useServerSideTableData: that hook returns a new
  // config object (and a new onDataRequest closure) on every render, which
  // re-triggers the table's internal fetch effect and produces duplicate
  // requests. A memoised config keeps the identity stable.
  const tableServerSide = useMemo(
    () => ({ enabled: true, onDataRequest: fetchUsers, debounceMs: 500 }),
    [fetchUsers]
  );

  // Handle user actions
  const handleViewUser = (userId) => {
    const user = rowsRef.current.find((u) => u.id === userId);
    navigate(`/admin/users/view/${userId}`, {
      state: { userName: user?.name }
    });
  };

  const handleEditUser = (userId) => {
    const user = rowsRef.current.find((u) => u.id === userId);
    navigate(`/admin/users/edit/${userId}`, {
      state: { userName: user?.name }
    });
  };

  const handleDeleteClick = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteUser(userToDelete.id);
      if (response.status === 204 || response.success) {
        toast.success(
          `User "${userToDelete.name}" has been deleted successfully`
        );
        refreshTable();
        setDeleteDialogOpen(false);
      } else {
        throw new Error(response.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.message || "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const columns = useMemo(() => {
    return [
      {
        id: "serialNumber",
        header: "S.No",
        accessorFn: (row, index) => index + 1,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 60,
      },
      {
        id: "name",
        header: "User Name",
        accessorKey: "name",
        accessorFn: (user) => user.name,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "name",
        size: 200,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex items-center justify-start gap-2">
              {user?.profileImage ? (
                <div className="rounded-md text-white w-8 h-8 flex items-center justify-center font-bold">
                  <img
                    src={user.profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover object-center rounded-md"
                  />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full text-sm">
                  <div className="w-full h-full flex items-center justify-center bg-[#5D93A5] rounded-full text-white font-medium text-md">
                    {getInitials(user.name)}
                  </div>
                </div>
              )}
              <span className="truncate font-medium">{user.name}</span>
            </div>
          );
        },
        exportConfig: {
          exportHeader: "User Name",
          exportValueFn: (user) => user.name,
        },
      },
      {
        id: "email",
        header: "Email",
        accessorKey: "email",
        accessorFn: (user) => user.email,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "email",
        size: 250,
        exportConfig: {
          exportHeader: "Email",
          exportValueFn: (user) => user.email,
        },
      },
      {
        id: "cdcNumber",
        header: "INDoS Number",
        accessorKey: "cdcNumber",
        accessorFn: (user) => user.cdcNumber,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "indianCdc",
        size: 150,
        exportConfig: {
          exportHeader: "INDoS Number",
          exportValueFn: (user) => user.cdcNumber,
        },
      },
      {
        id: "mobileNumber",
        header: "Mobile Number",
        accessorKey: "mobileNumber",
        accessorFn: (user) => user.mobileNumber,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "mobileNumber",
        serverSortKey: "mobileNumber",
        size: 200,
        exportConfig: {
          exportHeader: "Mobile Number",
          exportValueFn: (user) => user.mobileNumber,
        },
      },
      {
        id: "age",
        header: "Age",
        accessorKey: "age",
        accessorFn: (user) => user.age,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "age",
        enableSorting: true,
        serverSortKey: "age",
        size: 100,
        exportConfig: {
          exportHeader: "Age",
          exportValueFn: (user) => user.age,
        },
      },
      {
        id: "gender",
        header: "Gender",
        accessorKey: "gender",
        accessorFn: (user) => user.gender,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Male", value: "male" },
          { label: "Female", value: "female" },
          { label: "Other", value: "other" },
        ],
        serverFilterKey: "gender",
        serverSortKey: "gender",
        size: 100,
        cell: ({ getValue }) => {
          const gender = getValue();
          return (
            <span className="capitalize">{gender === "-" ? "-" : gender}</span>
          );
        },
        exportConfig: {
          exportHeader: "Gender",
          exportValueFn: (user) => user.gender,
        },
      },
      {
        id: "rank",
        header: "Rank",
        accessorKey: "rank",
        accessorFn: (user) => user.rank,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: RANK_LABELS.map((label) => ({
          label,
          value: label,
        })),
        serverFilterKey: "rankDesignation",
        serverSortKey: "rankDesignation",
        size: 150,
        cell: ({ getValue }) => {
          const rank = getValue();
          return (
            <span className="capitalize">
              {rank === "-" ? "-" : rank.replace(/_/g, " ")}
            </span>
          );
        },
        exportConfig: {
          exportHeader: "Rank",
          exportValueFn: (user) => user.rank,
        },
      },
      {
        id: "role",
        header: "Role",
        accessorKey: "role",
        accessorFn: (user) => user.role,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Super Admin", value: "SUPER_ADMIN" },
          { label: "Admin", value: "ADMIN" },
          { label: "Seafarer", value: "SEAFARER" },
        ],
        serverFilterKey: "role",
        serverSortKey: "role",
        size: 120,
        cell: ({ getValue }) => {
          const role = getValue();
          const roleColors = {
            super_admin: "bg-red-100 text-red-800",
            admin: "bg-blue-100 text-blue-800",
            seafarer: "bg-green-100 text-green-800",
          };
          return (
            <Badge className={roleColors[role] || "bg-gray-100 text-gray-800"}>
              {role.replace(/_/g, " ").toUpperCase()}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Role",
          exportValueFn: (user) => user.role,
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        accessorFn: (user) => user.status,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [{ label: "Banned", value: "banned" }],
        serverFilterKey: "status",
        serverSortKey: "isActive",
        size: 100,
        cell: ({ getValue }) => {
          const status = getValue();
          const statusColors = {
            active: "bg-green-100 text-green-800",
            inactive: "bg-yellow-100 text-yellow-800",
            banned: "bg-red-100 text-red-800",
          };
          return (
            <Badge
              className={statusColors[status] || "bg-gray-100 text-gray-800"}
            >
              {status.toUpperCase()}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Status",
          exportValueFn: (user) => user.status,
        },
      },
      {
        id: "emailVerified",
        header: "Email Status",
        accessorKey: "emailVerified",
        accessorFn: (user) => user.emailVerified,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Verified", value: "verified" },
          { label: "Unverified", value: "unverified" },
        ],
        serverFilterKey: "emailVerified",
        serverSortKey: "emailVerified",
        size: 120,
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge
              className={
                status === "verified"
                  ? "bg-green-100 text-green-800"
                  : "bg-orange-100 text-orange-800"
              }
            >
              {status.toUpperCase()}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Email Status",
          exportValueFn: (user) => user.emailVerified,
        },
      },
      {
        id: "accountSetup",
        header: "Account Setup",
        accessorKey: "accountSetup",
        accessorFn: (user) => user.accountSetup,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        filterType: "select",
        filterOptions: [
          { label: "Completed", value: "completed" },
          { label: "Pending", value: "pending" },
        ],
        serverFilterKey: "accountSetup",
        serverSortKey: "accountSetupCompleted",
        size: 140,
        cell: ({ getValue }) => {
          const status = getValue();
          return (
            <Badge
              className={
                status === "completed"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }
            >
              {status.toUpperCase()}
            </Badge>
          );
        },
        exportConfig: {
          exportHeader: "Account Setup",
          exportValueFn: (user) => user.accountSetup,
        },
      },
      {
        id: "aadharNumber",
        header: "Aadhar Number",
        accessorKey: "aadharNumber",
        accessorFn: (user) => user.aadharNumber,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "aadhaarNumber",
        size: 150,
        cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
        exportConfig: {
          exportHeader: "Aadhar Number",
          exportValueFn: (user) => user.aadharNumber,
        },
      },
      {
        id: "loginCount",
        header: "Login Count",
        accessorKey: "loginCount",
        accessorFn: (user) => user.loginCount,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: true,
        size: 120,
        exportConfig: {
          exportHeader: "Login Count",
          exportValueFn: (user) => user.loginCount,
        },
      },
      {
        id: "ship",
        header: "Current Ship",
        accessorKey: "ship",
        accessorFn: (user) => user.ship,
        enableGlobalFilter: true,
        enableColumnFilter: true,
        filterType: "text",
        serverFilterKey: "search",
        serverSortKey: "currentShipName",
        size: 150,
        exportConfig: {
          exportHeader: "Current Ship",
          exportValueFn: (user) => user.ship,
        },
      },
      {
        id: "lastActive",
        header: "Last Active",
        accessorKey: "lastActive",
        accessorFn: (user) => user.lastActive,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date",
        serverFilterKey: "lastLoginAfter",
        serverSortKey: "lastLoginAt",
        size: 150,
        cell: ({ getValue }) => {
          const date = getValue();
          if (date === "-") return "-";
          return new Date(date).toLocaleDateString();
        },
        exportConfig: {
          exportHeader: "Last Active",
          exportValueFn: (user) => user.lastActive,
        },
      },
      {
        id: "joinedDate",
        header: "Joined Date",
        accessorKey: "joinedDate",
        accessorFn: (user) => user.joinedDate,
        enableGlobalFilter: false,
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date",
        serverFilterKey: "createdAfter",
        serverSortKey: "createdAt",
        size: 150,
        cell: ({ getValue }) => {
          return new Date(getValue()).toLocaleDateString();
        },
        exportConfig: {
          exportHeader: "Joined Date",
          exportValueFn: (user) => user.joinedDate,
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "actions",
        enableGlobalFilter: false,
        enableColumnFilter: false,
        enableSorting: false,
        size: 100,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {canReadUser && (
                  <DropdownMenuItem
                    onClick={() => handleViewUser(user.id)}
                    className="cursor-pointer"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    <span>View Details</span>
                  </DropdownMenuItem>
                )}
                {canUpdateUser && (
                  <DropdownMenuItem
                    onClick={() => handleEditUser(user.id)}
                    className="cursor-pointer"
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    <span>Edit User</span>
                  </DropdownMenuItem>
                )}
                {canDeleteUser && (
                  <>
                    {(canReadUser || canUpdateUser) && <DropdownMenuSeparator />}
                    <DropdownMenuItem
                      className="cursor-pointer text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(user.id, user.name);
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      <span>Delete User</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        exportConfig: {
          excludeFromExport: true,
        },
      },
    ];
  }, [canCreateUser, canUpdateUser, canDeleteUser, canReadUser]);

  // If user doesn't have READ permission, show message
  if (!canReadUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view users.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="overflow-hidden font-sans">
          <div className="w-full flex flex-col gap-4 pr-6">
            <section className="w-full flex flex-col border bg-white p-4 rounded-xl shadow-xl gap-4">
              <div className="flex justify-between items-center">
                <h3 className="uppercase text-lg text-neutral-800 font-bold">
                  Users Directory
                </h3>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  {canCreateUser && (
                    <Button onClick={() => navigate("/admin/users/new")}>
                      <UserPlus className="mr-2 h-4 w-4" />
                      Add User
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <EnhancedDataTable
                  key={`users-table-${tableRefreshKey}`}
                  data={[]}
                  columns={columns}
                  emptyMessage="No users found"
                  enableDateRangeFilter={true}
                  dateFilterField="joinedDate"
                  enableGlobalSearch={true}
                  globalSearchPlaceholder="Search users..."
                  enableColumnVisibility={true}
                  enablePagination={true}
                  initialPageSize={10}
                  pageSizeOptions={[5, 10, 20, 50, 100]}
                  enableCSVExport={true}
                  enableCustomViews={true}
                  viewStorageKey={"users-management-view"}
                  onViewChange={(view) => {
                    console.log("Users view changed:", view);
                  }}
                  serverSide={tableServerSide}
                  theme="light"
                />
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <DialogTitle>Delete User</DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              Are you sure you want to delete user "{userToDelete?.name}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-end">
            <Button
              variant="outline"
              onClick={handleDialogClose}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
